import { useTranslation } from "react-i18next";
import { Grid as MuiGrid, Stack as MuiStack, Box as MuiBox, Box, Stack } from "@mzc-pdc/ui";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { alpha } from "@mui/material";
import theme from "@styles/theme";
import { IconZoomIn, IconZoomOut, IconArrowBack, IconArrowForward } from "@mzc-cloudplex/icons";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import styled from "styled-components";
import { useRef, useState, useEffect, useMemo } from "react";
import MediaImageWrapper from "@routes/asset/components/media-image-wrapper";
import { MediaTypeImage } from "@components/mediaTypeImage";
import AssetPreviewByStatus from "@components/property-panel/components/asset-preview-by-status";
import useAssetPreview from "@hooks/useAssetPreview";

const PreviewContainer = styled(MuiStack)`
    gap: ${theme.spacing(2.5)};
    flex: 1;
    height: 100%;
    width: 100%;
`;

const ImageInfoContainer = styled(MuiGrid)`
    flex: 0;
    padding: ${theme.spacing(1.5)};
    border: 1px solid ${theme.palette.greySecondary[100]};
    border-radius: ${theme.spacing(0.5)};
`;

const ImageContainer = styled(MuiGrid)`
    flex: 1;
    display: flex;
    flex-direction: column !important;
    gap: ${theme.spacing(2.5)};
`;

const ImageWrapper = styled(MuiBox)`
    background-color: ${alpha(theme.palette.greySecondary.main, 0.12)};
    flex: 1;
    border-radius: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1.5)};
`;

const PreviewImage = styled.img`
    object-fit: contain;
    max-width: 100%;
    max-height: 100%;
    margin: 0 auto;
    width: auto;
    height: auto;
`;

const ControlButtonContainer = styled(MuiStack)`
    justify-content: center;
    color: ${theme.palette.greySecondary.main};
    background-color: ${alpha(theme.palette.greySecondary[50], 0.24)};
    gap: ${theme.spacing(0.25)};
    height: 40px;
    position: absolute;
    top: 0;
    right: 0;
`;

const ElementsContainer = styled(MuiGrid)`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-right: ${theme.spacing(12.5)};
    margin-left: ${theme.spacing(12.5)};
    overflow-x: auto;
    flex-wrap: nowrap;
`;

const BottomContainer = styled(MuiGrid)`
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    min-height: 80px;
`;

const ThumbnailGrid = styled(MuiGrid)`
    width: 80px;
    height: 80px;
    background-color: ${theme.palette.background.default};
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid ${(props) => (props.isSelected ? theme.palette.primary.main : theme.palette.greySecondary[100])};
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    flex-shrink: 0;
`;

const ThumbnailImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
`;

const MinimapContainer = styled(MuiBox)`
    position: absolute;
    bottom: 20px;
    left: 0px;
    width: 150px;
    height: 150px;
    background-color: ${alpha(theme.palette.common.white, 0.9)};
    border: 1px solid ${theme.palette.greySecondary[300]};
    border-radius: ${theme.spacing(0.5)};
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
`;

const MinimapImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const ViewportIndicator = styled(MuiBox)`
    position: absolute;
    border: 2px solid ${theme.palette.primary.main};
    background-color: ${alpha(theme.palette.primary.main, 0.1)};
    pointer-events: none;
`;

const MinimapWrapper = styled(MuiBox)`
    position: relative;
    width: 100%;
    height: 100%;
`;

const ImageNavigationButton = styled(MuiBox)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    background-color: ${alpha(theme.palette.constant.black, 0.3)};
    border-radius: 50%;
    color: ${theme.palette.constant.white};
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    z-index: 10;
    pointer-events: auto;

    &:hover {
        background-color: ${alpha(theme.palette.greySecondary[50], 0.8)};
    }
`;

const ImageNavigationContainer = styled(MuiBox)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 5;
    pointer-events: none;

    &:hover ${ImageNavigationButton} {
        opacity: 1;
    }
`;

export const IncompleteMediaDisplay = ({
    state,
    status,
    mediaType,
    variant = "rectangular",
    width = 46,
    height = 56,
    sx = {},
}) => {
    return (
        <MediaImageWrapper
            status={status}
            variant={variant}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                ...sx,
            }}
        >
            <AssetPreviewByStatus
                previewStatus={status}
                state={state}
                mediaType={mediaType || "image"}
                width={width}
                height={height}
            />
        </MediaImageWrapper>
    );
};

const ThumbnailItem = ({ element, index, isSelected, state, onClick, getElementThumbnailUrl, getPreviewStatus }) => {
    const status = getPreviewStatus(element.previews);

    if (status !== "COMPLETE") {
        return (
            <MediaImageWrapper
                key={index}
                status={status}
                sx={{
                    width: 80,
                    height: 80,
                    position: `relative`,
                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                    cursor: "pointer",
                    border: isSelected
                        ? `3px solid ${theme.palette.primary.main}`
                        : `3px solid ${theme.palette.greySecondary[100]}`,
                }}
                onClick={onClick}
            >
                <MediaTypeImage
                    mediaType="image"
                    size={"small"}
                    errorIconProps={{ position: "right", size: 16 }}
                    progressIconProps={{ isTooltip: true }}
                    refreshButton={true}
                    isPreferred={element.isPreferred}
                    previewStatus={status}
                    sx={{ borderRadius: "4px" }}
                    width={28}
                    height={28}
                    state={state}
                />
            </MediaImageWrapper>
        );
    }
    return (
        <ThumbnailGrid key={index} item isSelected={isSelected} onClick={onClick}>
            <ThumbnailImage
                src={getElementThumbnailUrl(element.previews)}
                alt={element.name || `thumbnail-${index}`}
                draggable="false"
            />
        </ThumbnailGrid>
    );
};

const NavigationButtons = ({ isVisible, elements, isHovering, onPrev, onNext }) => {
    if (elements.length <= 1) return null;

    return (
        <ImageNavigationContainer>
            <ImageNavigationButton style={{ left: theme.spacing(3), opacity: isHovering ? 1 : 0 }} onClick={onPrev}>
                <IconArrowBack size={20} />
            </ImageNavigationButton>
            <ImageNavigationButton style={{ right: theme.spacing(6), opacity: isHovering ? 1 : 0 }} onClick={onNext}>
                <IconArrowForward size={20} />
            </ImageNavigationButton>
        </ImageNavigationContainer>
    );
};

const ZoomableImageViewer = ({ elements = [], asset }) => {
    const { t } = useTranslation();
    const [minimapVisible, setMinimapVisible] = useState(false);
    const minimapRef = useRef(null);
    const transformRef = useRef(null);
    const [imageInfo, setImageInfo] = useState(null);
    const [selectedElementIndex, setSelectedElementIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const { getElementThumbnailUrl, getPreviewStatus } = useAssetPreview({
        mediaType: asset?.mediaType,
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const selectedImageUrl = useMemo(() => {
        if (elements.length > 0) {
            const element = elements[selectedElementIndex];
            const largestElementPreview = getElementThumbnailUrl(element.previews);

            if (!largestElementPreview && element.accessUrl) return element?.accessUrl;
            return largestElementPreview;
        }
        return "";
    }, [elements, selectedElementIndex]);

    // 미니맵 내 영역 표시기 위치 계산
    const calculateViewportPosition = (state) => {
        if (!minimapRef.current || !imageInfo) return { width: 0, height: 0, x: 0, y: 0 };

        const { scale, positionX, positionY } = state;

        // 미니맵 크기
        const minimapWidth = minimapRef.current.clientWidth;
        const minimapHeight = minimapRef.current.clientHeight;

        // 이미지 원본 크기
        const imageWidth = imageInfo.width;
        const imageHeight = imageInfo.height;

        // 컨테이너 크기
        const containerElement = document.querySelector(".react-transform-component");
        if (!containerElement) return { width: 0, height: 0, x: 0, y: 0 };

        const containerWidth = containerElement.clientWidth;
        const containerHeight = containerElement.clientHeight;

        // 확대된 이미지 크기
        const scaledImageWidth = imageWidth * scale;
        const scaledImageHeight = imageHeight * scale;

        // 미니맵에서의 이미지 크기와 위치 계산 (letterboxing 적용)
        let minimapImageWidth, minimapImageHeight;
        let minimapImageX = 0,
            minimapImageY = 0;

        const minimapRatio = minimapWidth / minimapHeight;
        const imageRatio = imageWidth / imageHeight;

        if (imageRatio > minimapRatio) {
            // 이미지가 가로로 더 긴 경우
            minimapImageWidth = minimapWidth;
            minimapImageHeight = minimapWidth / imageRatio;
            minimapImageY = (minimapHeight - minimapImageHeight) / 2; //높이 기준 중앙 정렬
        } else {
            // 이미지가 세로로 더 긴 경우
            minimapImageHeight = minimapHeight;
            minimapImageWidth = minimapHeight * imageRatio;
            minimapImageX = (minimapWidth - minimapImageWidth) / 2; //너비 기준 중앙 정렬
        }

        // 이미지가 컨테이너보다 작은 경우 처리 (전체 이미지가 보이는 경우)
        if (scaledImageWidth <= containerWidth && scaledImageHeight <= containerHeight) {
            // 미니맵에 전체 영역을 표시
            return {
                width: minimapImageWidth,
                height: minimapImageHeight,
                x: minimapImageX,
                y: minimapImageY,
            };
        }

        // 이미지가 컨테이너보다 큰 경우 (일부만 보이는 경우)
        // 뷰포트 크기 계산 (컨테이너 크기 / 확대된 이미지 크기)
        let viewportWidthRatio = Math.min(containerWidth / scaledImageWidth, 1);
        let viewportHeightRatio = Math.min(containerHeight / scaledImageHeight, 1);

        // 최소 크기 제한 (이미지가 매우 확대되었을 경우)
        const minViewportSize = 1; // 미니맵에 표시될 최소 크기 (픽셀)

        // 미니맵 내 영역 표시기 크기 계산
        let viewportWidth = minimapImageWidth * viewportWidthRatio;
        let viewportHeight = minimapImageHeight * viewportHeightRatio;

        // 최소 크기 보장
        viewportWidth = Math.max(viewportWidth, minViewportSize);
        viewportHeight = Math.max(viewportHeight, minViewportSize);

        // react-zoom-pan-pinch에서 positionX, positionY는 이미지 위치를 나타냅니다
        // 화면 중앙이 (0,0)이고, 이미지가 오른쪽으로 이동하면 positionX는 양수, 왼쪽으로 이동하면 음수
        // 이미지가 아래로 이동하면 positionY는 양수, 위로 이동하면 음수

        // 이미지의 중심이 컨테이너 중심에 있을 때 positionX, positionY는 0
        // 화면에 보이는 영역의 좌상단 좌표 계산
        const viewportLeft = (scaledImageWidth - containerWidth) / 2 - positionX;
        const viewportTop = (scaledImageHeight - containerHeight) / 2 - positionY;

        // 미니맵에서의 비율로 변환
        const minimapViewportLeft = (viewportLeft / scaledImageWidth) * minimapImageWidth + minimapImageX;
        const minimapViewportTop = (viewportTop / scaledImageHeight) * minimapImageHeight + minimapImageY;

        // 경계 확인 (미니맵 이미지 영역을 벗어나지 않도록)
        let viewportX = Math.max(minimapViewportLeft, minimapImageX);
        viewportX = Math.min(viewportX, minimapImageX + minimapImageWidth - viewportWidth);

        let viewportY = Math.max(minimapViewportTop, minimapImageY);
        viewportY = Math.min(viewportY, minimapImageY + minimapImageHeight - viewportHeight);

        return {
            width: viewportWidth,
            height: viewportHeight,
            x: viewportX,
            y: viewportY,
        };
    };

    // 미니맵 클릭 위치로 이동
    const handleMinimapClick = (e) => {
        if (!transformRef.current || !minimapRef.current || !imageInfo) return;

        // 미니맵 내에서의 클릭 좌표
        const rect = minimapRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // 이미지 원본 크기
        const imageWidth = imageInfo.width;
        const imageHeight = imageInfo.height;

        // 미니맵 크기
        const minimapWidth = minimapRef.current.clientWidth;
        const minimapHeight = minimapRef.current.clientHeight;

        // 미니맵에서의 이미지 크기와 위치 계산
        let minimapImageWidth, minimapImageHeight;
        let minimapImageX = 0,
            minimapImageY = 0;

        const minimapRatio = minimapWidth / minimapHeight;
        const imageRatio = imageWidth / imageHeight;

        if (imageRatio > minimapRatio) {
            // 이미지가 가로로 더 긴 경우
            minimapImageWidth = minimapWidth;
            minimapImageHeight = minimapWidth / imageRatio;
            minimapImageY = (minimapHeight - minimapImageHeight) / 2;
        } else {
            // 이미지가 세로로 더 긴 경우
            minimapImageHeight = minimapHeight;
            minimapImageWidth = minimapHeight * imageRatio;
            minimapImageX = (minimapWidth - minimapImageWidth) / 2;
        }

        // 클릭 좌표가 미니맵 내 이미지 영역을 벗어났는지 확인
        if (
            x < minimapImageX ||
            x > minimapImageX + minimapImageWidth ||
            y < minimapImageY ||
            y > minimapImageY + minimapImageHeight
        ) {
            return; // 이미지 영역 외부 클릭은 무시
        }

        // 현재 확대/축소 상태
        const { scale } = transformRef.current.state;

        // 컨테이너 크기
        const containerElement = document.querySelector(".react-transform-component");
        if (!containerElement) return;

        const containerWidth = containerElement.clientWidth;
        const containerHeight = containerElement.clientHeight;

        // 확대된 이미지 크기
        const scaledImageWidth = imageWidth * scale;
        const scaledImageHeight = imageHeight * scale;

        // 이미지가 컨테이너보다 작은 경우 이동할 필요 없음
        if (scaledImageWidth <= containerWidth && scaledImageHeight <= containerHeight) {
            return;
        }

        // 클릭 위치를 이미지 내 상대적 위치로 변환 (0~1)
        const imageX = (x - minimapImageX) / minimapImageWidth;
        const imageY = (y - minimapImageY) / minimapImageHeight;

        // 중심 좌표 기준으로 변환
        const targetCenterX = (imageX - 0.5) * scaledImageWidth;
        const targetCenterY = (imageY - 0.5) * scaledImageHeight;

        // 음수로 설정하여 이미지를 반대로 이동 (이미지를 왼쪽으로 이동하려면 positionX는 양수)
        transformRef.current.setTransform(-targetCenterX, -targetCenterY, scale);
    };

    // 썸네일 이미지 선택 처리
    const handleThumbnailSelect = (index) => {
        setSelectedElementIndex(index);
        // 이미지가 변경되면 확대/축소 상태 초기화
        if (transformRef.current) {
            transformRef.current.resetTransform();
        }
    };

    // 이전 이미지로 이동
    const handlePrevElement = (e) => {
        e.stopPropagation();
        const prevIndex = selectedElementIndex > 0 ? selectedElementIndex - 1 : elements.length - 1;
        handleThumbnailSelect(prevIndex);
    };

    // 다음 이미지로 이동
    const handleNextElement = (e) => {
        e.stopPropagation();
        const nextIndex = selectedElementIndex < elements.length - 1 ? selectedElementIndex + 1 : 0;
        handleThumbnailSelect(nextIndex);
    };

    // 마우스 호버 이벤트 핸들러
    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    // 이미지 로드 시 원본 크기 정보 저장
    useEffect(() => {
        if (!selectedImageUrl) return;

        const img = new Image();
        img.onload = () => {
            setImageInfo({
                width: img.width,
                height: img.height,
            });
        };
        img.src = selectedImageUrl;
    }, [selectedImageUrl]);

    useEffect(() => {
        setSelectedElementIndex(0);
    }, [elements]);

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <PreviewContainer direction={"column"}>
            <ImageContainer>
                {asset?.state === "DRAFT" ||
                (elements[selectedElementIndex] &&
                    getPreviewStatus(elements[selectedElementIndex].previews) !== "COMPLETE") ? (
                    <Box
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        sx={{
                            flex: 1,
                            backgroundColor: alpha(theme.palette.greySecondary.main, 0.12),
                            borderRadius: theme.spacing(1.5),
                            padding: theme.spacing(1.5),
                            display: "flex",
                            justifyContent: "center",
                            height: "100%",
                            position: "relative",
                        }}
                    >
                        <IncompleteMediaDisplay
                            element={elements[selectedElementIndex]}
                            state={asset?.state}
                            mediaType={asset?.mediaType}
                            status={getPreviewStatus(elements[selectedElementIndex].previews)}
                            sx={{
                                width: "100%",
                                height: "100%",
                                flexDirection: "column",
                            }}
                        />

                        <NavigationButtons
                            elements={elements}
                            isHovering={isHovering}
                            onPrev={handlePrevElement}
                            onNext={handleNextElement}
                        />
                    </Box>
                ) : (
                    <TransformWrapper
                        minScale={0.1}
                        initialScale={1}
                        initialPositionX={0}
                        initialPositionY={0}
                        centerOnInit={true}
                        ref={transformRef}
                    >
                        {({ zoomIn, zoomOut, resetTransform, state }) => (
                            <>
                                <ImageWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                                    <TransformComponent
                                        wrapperStyle={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: theme.spacing(1.5),
                                        }}
                                        contentStyle={{
                                            width: "100%",
                                            height: `calc(${windowHeight}px - 300px)`,
                                        }}
                                    >
                                        {selectedImageUrl && (
                                            <PreviewImage
                                                src={selectedImageUrl}
                                                alt={elements[selectedElementIndex]?.name}
                                            />
                                        )}
                                    </TransformComponent>

                                    <NavigationButtons
                                        elements={elements}
                                        isHovering={isHovering}
                                        onPrev={handlePrevElement}
                                        onNext={handleNextElement}
                                    />

                                    {/* 미니맵 */}
                                    {minimapVisible && imageInfo && selectedImageUrl && (
                                        <MinimapContainer className="minimap-container">
                                            <MinimapWrapper ref={minimapRef} onClick={handleMinimapClick}>
                                                <MinimapImage src={selectedImageUrl} alt="미니맵" />
                                                <ViewportIndicator
                                                    style={{
                                                        width: `${calculateViewportPosition(state).width}px`,
                                                        height: `${calculateViewportPosition(state).height}px`,
                                                        left: `${calculateViewportPosition(state).x}px`,
                                                        top: `${calculateViewportPosition(state).y}px`,
                                                    }}
                                                />
                                            </MinimapWrapper>
                                        </MinimapContainer>
                                    )}
                                </ImageWrapper>
                                <BottomContainer>
                                    <ElementsContainer>
                                        <Stack direction={"row"} gap={theme.spacing(1.5)}>
                                            {elements.map((element, index) => (
                                                <ThumbnailItem
                                                    key={index}
                                                    element={element}
                                                    index={index}
                                                    state={state}
                                                    isSelected={selectedElementIndex === index}
                                                    onClick={() => handleThumbnailSelect(index)}
                                                    getElementThumbnailUrl={getElementThumbnailUrl}
                                                    getPreviewStatus={getPreviewStatus}
                                                />
                                            ))}
                                        </Stack>
                                    </ElementsContainer>
                                    <ControlButtonContainer direction={"row"}>
                                        <TooltipButton
                                            isResponsive={true}
                                            circled
                                            variant={"text"}
                                            size={"large"}
                                            onClickButton={zoomIn}
                                            buttonIcon={<IconZoomIn size={16} />}
                                            title={t(`common::label::Zoom in`)}
                                        />
                                        <TooltipButton
                                            isResponsive={true}
                                            circled
                                            variant={"text"}
                                            size={"large"}
                                            onClickButton={zoomOut}
                                            buttonIcon={<IconZoomOut size={16} />}
                                            title={t(`common::label::Zoom out`)}
                                        />
                                    </ControlButtonContainer>
                                </BottomContainer>
                            </>
                        )}
                    </TransformWrapper>
                )}
            </ImageContainer>
        </PreviewContainer>
    );
};

export default ZoomableImageViewer;
